import React, { useState, useRef, useEffect } from 'react';
import { CustomSelect } from '../../../../../shared/components/CustomSelect';
import { productCompactDisplayLabel } from '../../../../../shared/utils/productDisplayLabel';
import { useTeamTab } from '../../TeamTabContext';
import type { OrgRbacMemberRow } from '../../TeamTabContext';
import type { Product } from '../../../../../contexts/AuthContext';

function formatOrgRbacDate(raw: string | null | undefined): string {
  if (!raw) return '\u2014';
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return String(raw);
  }
}

export const OrgRbacMembersPanel: React.FC = () => {
  const {
    currentOrg,
    orgRbacError,
    orgRbacLoading,
    orgRbacMembers,
    orgRbacRoleDraft,
    setOrgRbacRoleDraft,
    orgRbacSavingUserId,
    orgRbacOwnershipConfirm,
    setOrgRbacOwnershipConfirm,
    handleOrgRbacSaveRow,
    executeOrgRbacPatch,
    orgMemberRemoveLoading,
    setOrgMemberRemoveTarget,
    visibleProductsSettings,
    profileApiFetch,
    loadOrgRbacMembers,
  } = useTeamTab();

  if (currentOrg?.role !== 'owner') return null;

  const orgId = currentOrg?.id;

  return (
    <div id="profile-org-rbac-members" className="settings-panel">
      <div className="settings-panel__header">
        <h3 className="settings-panel__title">Organization members &amp; roles</h3>
        <p className="settings-panel__description">
          Relix login accounts in this organization. <strong>Joined</strong> is when they were added
          to the org; <strong>Email confirmed</strong> comes from the auth provider when they
          verified their email. Promoting someone to <strong>Owner</strong> transfers ownership from
          you — you become <strong>Administrator</strong>.
        </p>
      </div>

      {orgRbacError && (
        <div className="settings-callout settings-callout--danger" style={{ marginBottom: '12px' }}>
          <div className="settings-callout__body">{orgRbacError}</div>
        </div>
      )}

      {orgRbacLoading ? (
        <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>Loading members…</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '13px',
              minWidth: 780,
            }}
          >
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--color-text-secondary)' }}>
                <th
                  style={{ padding: '8px', borderBottom: '1px solid var(--profile-nested-border)' }}
                >
                  Email
                </th>
                <th
                  style={{ padding: '8px', borderBottom: '1px solid var(--profile-nested-border)' }}
                >
                  Display name
                </th>
                <th
                  style={{ padding: '8px', borderBottom: '1px solid var(--profile-nested-border)' }}
                >
                  Joined org
                </th>
                <th
                  style={{ padding: '8px', borderBottom: '1px solid var(--profile-nested-border)' }}
                >
                  Email confirmed
                </th>
                <th
                  style={{ padding: '8px', borderBottom: '1px solid var(--profile-nested-border)' }}
                >
                  Products
                </th>
                <th
                  style={{ padding: '8px', borderBottom: '1px solid var(--profile-nested-border)' }}
                >
                  RBAC role
                </th>
                <th
                  style={{ padding: '8px', borderBottom: '1px solid var(--profile-nested-border)' }}
                >
                  Save
                </th>
                <th
                  style={{ padding: '8px', borderBottom: '1px solid var(--profile-nested-border)' }}
                >
                  Remove
                </th>
              </tr>
            </thead>
            <tbody>
              {orgRbacMembers.map((m: OrgRbacMemberRow) => {
                const uid = m.user_id;
                const draft = (orgRbacRoleDraft[uid] || 'member').toLowerCase();
                const live = String(m.role || '').toLowerCase();
                const dirty = draft !== live;
                return (
                  <tr key={uid}>
                    <td style={{ padding: '8px', verticalAlign: 'middle' }}>
                      {m.email?.trim() || '\u2014'}
                    </td>
                    <td style={{ padding: '8px', verticalAlign: 'middle' }}>
                      {m.display_name?.trim() || '\u2014'}
                    </td>
                    <td style={{ padding: '8px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      {formatOrgRbacDate(m.joined_at)}
                    </td>
                    <td style={{ padding: '8px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      {formatOrgRbacDate(m.email_confirmed_at)}
                    </td>
                    <td style={{ padding: '8px', verticalAlign: 'middle' }}>
                      <ProductChipsCellInner
                        member={m}
                        orgProducts={visibleProductsSettings}
                        orgId={orgId!}
                        profileApiFetch={profileApiFetch}
                        onSaved={loadOrgRbacMembers}
                      />
                    </td>
                    <td style={{ padding: '8px', verticalAlign: 'middle' }}>
                      <CustomSelect
                        style={{ minWidth: 140 }}
                        value={draft}
                        options={[
                          { value: 'owner', label: 'Owner' },
                          { value: 'administrator', label: 'Administrator' },
                          { value: 'member', label: 'Member' },
                        ]}
                        onChange={(val) => setOrgRbacRoleDraft((prev) => ({ ...prev, [uid]: val }))}
                        disabled={orgRbacSavingUserId === uid}
                      />
                    </td>
                    <td style={{ padding: '8px', verticalAlign: 'middle' }}>
                      <button
                        type="button"
                        className="save-button"
                        style={{
                          padding: '6px 12px',
                          fontSize: '13px',
                          opacity: dirty ? 1 : 0.45,
                        }}
                        disabled={
                          !dirty || orgRbacSavingUserId === uid || !!orgRbacOwnershipConfirm
                        }
                        onClick={() => handleOrgRbacSaveRow(uid)}
                      >
                        {orgRbacSavingUserId === uid ? 'Saving\u2026' : 'Save'}
                      </button>
                    </td>
                    <td style={{ padding: '8px', verticalAlign: 'middle' }}>
                      <button
                        type="button"
                        className="save-button save-button--danger"
                        style={{
                          padding: '6px 12px',
                          fontSize: '13px',
                        }}
                        disabled={orgMemberRemoveLoading || !!orgRbacOwnershipConfirm}
                        onClick={() => setOrgMemberRemoveTarget(m)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Ownership transfer confirm modal */}
      {orgRbacOwnershipConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="org-rbac-transfer-title"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10002,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.45)',
            padding: 16,
          }}
          onClick={() => setOrgRbacOwnershipConfirm(null)}
        >
          <div
            style={{
              maxWidth: 440,
              width: '100%',
              padding: '20px 22px',
              borderRadius: 12,
              backgroundColor: 'var(--color-bg-surface)',
              border: '1px solid var(--color-border-strong)',
              boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4
              id="org-rbac-transfer-title"
              style={{
                margin: '0 0 10px 0',
                fontSize: '16px',
                fontWeight: 600,
                color: 'var(--color-text)',
              }}
            >
              Transfer organization ownership?
            </h4>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', lineHeight: 1.5 }}>
              <strong>{orgRbacOwnershipConfirm.displayLabel}</strong> will become{' '}
              <strong>Owner</strong>. Your role will change to <strong>Administrator</strong>. You
              can undo this only by having the new Owner promote you again.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="save-button"
                style={{
                  backgroundColor: 'transparent',
                  color: 'var(--color-text-secondary)',
                  border: '1px solid var(--color-border-strong)',
                }}
                onClick={() => setOrgRbacOwnershipConfirm(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="save-button"
                style={{ backgroundColor: '#b45309' }}
                onClick={() => void executeOrgRbacPatch(orgRbacOwnershipConfirm.userId, 'owner')}
              >
                Transfer ownership
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── ProductChipsCellInner ──────────────────────────────────────────────────

const CHIP_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
  padding: '2px 6px 2px 8px',
  borderRadius: 4,
  fontSize: '11px',
  fontWeight: 500,
  lineHeight: '16px',
  backgroundColor: 'var(--color-bg-muted, #f1f5f9)',
  color: 'var(--color-text-secondary)',
  border: '1px solid var(--color-border, #e2e8f0)',
  whiteSpace: 'nowrap',
};

const REMOVE_BTN_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 14,
  height: 14,
  border: 'none',
  background: 'transparent',
  color: 'var(--color-text-muted, #94a3b8)',
  cursor: 'pointer',
  borderRadius: 2,
  padding: 0,
  fontSize: '12px',
  lineHeight: 1,
  transition: 'color 0.15s, background-color 0.15s',
};

const ADD_BTN_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 22,
  height: 20,
  border: '1px dashed var(--color-border, #e2e8f0)',
  background: 'transparent',
  color: 'var(--color-text-muted, #94a3b8)',
  cursor: 'pointer',
  borderRadius: 4,
  padding: 0,
  fontSize: '13px',
  fontWeight: 600,
  lineHeight: 1,
  transition: 'color 0.15s, border-color 0.15s, background-color 0.15s',
};

function ProductChipsCellInner({
  member,
  orgProducts,
  orgId,
  profileApiFetch,
  onSaved,
}: {
  member: OrgRbacMemberRow;
  orgProducts: Product[];
  orgId: string;
  profileApiFetch: (path: string, init?: RequestInit) => Promise<Response>;
  onSaved: () => Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [dropdownOpen]);

  // Clear error after a timeout
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 4000);
    return () => clearTimeout(t);
  }, [error]);

  const currentProducts = member.products ?? [];
  const currentIds = new Set(currentProducts.map((p) => p.productId));

  // Products available to add (org products not yet assigned)
  const availableProducts = orgProducts.filter((p) => !currentIds.has(p.id));

  const saveProductIds = async (nextIds: string[]) => {
    setSaving(true);
    setError(null);
    try {
      const res = await profileApiFetch(
        `/api/organizations/${orgId}/members/${member.user_id}/products`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productIds: nextIds }),
        }
      );
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(data.error || 'Failed to update product access');
      await onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = (productId: string) => {
    const nextIds = currentProducts
      .filter((p) => p.productId !== productId)
      .map((p) => p.productId);
    void saveProductIds(nextIds);
  };

  const handleAdd = (productId: string) => {
    const nextIds = [...currentProducts.map((p) => p.productId), productId];
    setDropdownOpen(false);
    void saveProductIds(nextIds);
  };

  return (
    <div style={{ position: 'relative', minWidth: 80 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
        {currentProducts.length > 0
          ? currentProducts.map((pr) => (
              <span key={pr.productId} style={CHIP_STYLE}>
                {productCompactDisplayLabel({
                  name: pr.name,
                  jira_project_key: pr.jiraProjectKey,
                })}
                <button
                  type="button"
                  style={REMOVE_BTN_STYLE}
                  disabled={saving}
                  title="Remove product access"
                  onClick={() => handleRemove(pr.productId)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--color-danger, #dc2626)';
                    e.currentTarget.style.backgroundColor = 'rgba(220,38,38,0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--color-text-muted, #94a3b8)';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  &times;
                </button>
              </span>
            ))
          : !saving && (
              <span style={{ fontSize: '11px', color: 'var(--color-text-muted, #94a3b8)' }}>
                None
              </span>
            )}

        {saving && (
          <span
            style={{
              display: 'inline-block',
              width: 12,
              height: 12,
              border: '2px solid var(--color-border, #e2e8f0)',
              borderTopColor: 'var(--color-primary, #3b82f6)',
              borderRadius: '50%',
              animation: 'spin 0.6s linear infinite',
              flexShrink: 0,
            }}
          />
        )}

        {!saving && availableProducts.length > 0 && (
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            <button
              type="button"
              style={ADD_BTN_STYLE}
              title="Add product access"
              onClick={() => setDropdownOpen((v) => !v)}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--color-primary, #3b82f6)';
                e.currentTarget.style.borderColor = 'var(--color-primary, #3b82f6)';
                e.currentTarget.style.backgroundColor = 'rgba(59,130,246,0.06)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--color-text-muted, #94a3b8)';
                e.currentTarget.style.borderColor = 'var(--color-border, #e2e8f0)';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              +
            </button>
            {dropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: 4,
                  zIndex: 50,
                  minWidth: 180,
                  maxWidth: 260,
                  maxHeight: 200,
                  overflowY: 'auto',
                  backgroundColor: 'var(--color-bg-surface, #fff)',
                  border: '1px solid var(--color-border-strong, #cbd5e1)',
                  borderRadius: 8,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  padding: '4px 0',
                }}
              >
                {availableProducts.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '6px 12px',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      fontSize: '12px',
                      color: 'var(--color-text)',
                      cursor: 'pointer',
                      lineHeight: 1.4,
                    }}
                    onClick={() => handleAdd(p.id)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-bg-muted, #f1f5f9)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {productCompactDisplayLabel(p)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div
          style={{
            marginTop: 2,
            fontSize: '10px',
            color: 'var(--color-danger, #dc2626)',
            lineHeight: 1.3,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
